import React from 'react'
import styled from '@emotion/styled'
import { OrganisationType, UserTradingEntityDetails, User } from 'api'
import { OrganisationTradingEntityMultiCombobox } from 'view/shared/entity'
import { COUNTERPARTY_COLOURS } from 'view/routes/staging/SendRFQPanel/rfqPanelStyles'
import * as constants from 'view/styles/constants'
import { Locale, Translation } from 'locale'
import { useOrganisation } from 'view/hooks'
import { Table, TableColumn, TableOptions } from 'view/components/panels'
import { ToggleField } from 'view/components/toggle'
import { TradingEntityPublishedNameRenderer } from 'view/components/datagrid'
import { ClearButton } from 'view/components/options/optionsStyles'
import { DialogRow } from 'view/components/inputs'
import { HorizontalLayout } from 'view/styles/constants'

export interface TradingEntitiesPanelProps {
  user: User
  readOnly: boolean
  onUpdateUser: (user: Partial<User>) => void
}

const TagLine = styled.div`
  padding-left: 20px;
  font-family: NEXWeb;
  font-weight: ${constants.FontWeight.Medium};
  font-size: 14px;
  font-weight: 500;
  color: ${constants.CORE_PRIMARY_7};
  opacity: 0.6;
`

const ToggleFieldStyled = styled(ToggleField)`
  justify-content: flex-end;
`

const options: TableOptions<UserTradingEntityDetails> = { getRowId: row => row.tradingEntityId }

export const TradingEntitiesPanel: React.FC<TradingEntitiesPanelProps> = ({ user, readOnly, onUpdateUser }) => {
  const organisation = useOrganisation(user.organisationId)
  const isBuySide = organisation?.organisationTypes?.includes(OrganisationType.BUY_SIDE)

  const handleSwitchAllTradingEntities = (checked: boolean) => {
    onUpdateUser({ allTradingEntities: checked })
  }

  const handleUpdateTradingEntities = (options: string[]) => {
    onUpdateUser({
      tradingEntityDetails: options.map(option => {
        const found = user.tradingEntityDetails.find(t => t.tradingEntityId === option)
        const shouldCopyPassports = isBuySide && user.tradingEntityDetails.length > 0

        if (found) {
          return found
        }

        return {
          euPassport: shouldCopyPassports ? user.tradingEntityDetails?.[0]?.euPassport : null,
          niUk: shouldCopyPassports ? user.tradingEntityDetails?.[0]?.niUk : null,
          ukPassport: shouldCopyPassports ? user.tradingEntityDetails?.[0]?.ukPassport : null,
          tradingEntityId: option
        }
      })
    })
  }

  const handleUpdateAllDetails = <K extends keyof UserTradingEntityDetails>(key: K) => (value: UserTradingEntityDetails[K]) => {
    onUpdateUser({
      tradingEntityDetails: user.tradingEntityDetails.map(r => ({ ...r, [key]: value }))
    })
  }

  const handleUpdateDetail = <K extends keyof UserTradingEntityDetails>(key: K) => ({
    value,
    data,
    rows
  }: {
    value: UserTradingEntityDetails[K]
    data: UserTradingEntityDetails
    rows: UserTradingEntityDetails[]
  }) => {
    onUpdateUser({
      tradingEntityDetails: rows.map(r => {
        if (r.tradingEntityId === data.tradingEntityId) {
          return { ...r, [key]: value }
        }

        return r
      })
    })
  }

  const columns: TableColumn<UserTradingEntityDetails>[] = [
    {
      colId: 'TradingEntity',
      headerName: Locale.translate(Translation.TC_TRADING_ENTITY),
      styles: { flex: 3, left: true },
      valueGetter: ({ data }) => data.tradingEntityId,
      cellRenderer: ({ value }) => <TradingEntityPublishedNameRenderer value={value} />
    },
    {
      colId: 'PassportEU',
      headerName: Locale.translate(Translation.TC_PASSPORT_EU),
      cellRenderer: ({ data, rows }) => (
        <ToggleField
          label=""
          checked={data.euPassport ?? false}
          readOnly={readOnly || isBuySide}
          onUpdate={value => handleUpdateDetail('euPassport')({ value, data, rows })}
        />
      )
    },
    {
      colId: 'PassportUK',
      headerName: Locale.translate(Translation.TC_PASSPORT_UK),
      cellRenderer: ({ data, rows }) => (
        <ToggleField
          label=""
          checked={data.ukPassport ?? false}
          readOnly={readOnly || isBuySide}
          onUpdate={value => handleUpdateDetail('ukPassport')({ value, data, rows })}
        />
      )
    },
    {
      colId: 'NIUK',
      headerName: Locale.translate(Translation.TC_PASSPORT_NIUK),
      cellRenderer: ({ data, rows }) => (
        <ToggleField
          label=""
          checked={data.niUk ?? false}
          readOnly={readOnly || isBuySide}
          onUpdate={value => handleUpdateDetail('niUk')({ value, data, rows })}
        />
      )
    }
  ]

  return user.organisationId === null ? (
    <TagLine>{Locale.translate(Translation.SC_SELECT_ORGANISATION_MESSAGE)}</TagLine>
  ) : (
    <>
      <ToggleFieldStyled
        checked={user.allTradingEntities}
        onUpdate={handleSwitchAllTradingEntities}
        label={Locale.translate(Translation.SC_AUTOMATICALLY_ADD_NEW_ENTITIES)}
        readOnly={readOnly}
      />

      <OrganisationTradingEntityMultiCombobox
        readOnly={readOnly}
        organisationId={user.organisationId}
        placeholder={Locale.translate(Translation.SC_SELECT_TRADING_ENTITY)}
        optionColorPalette={COUNTERPARTY_COLOURS}
        value={user.tradingEntityDetails.map(d => d.tradingEntityId)}
        onUpdate={handleUpdateTradingEntities}
        showSelectedOptionsPane={false}
        dataTestId={'UserDialog-TradingEntitiesPanel-OrganisationTradingEntityMultiCombobox'}
      />

      {user.tradingEntityDetails.length > 0 && isBuySide && (
        <DialogRow horizontalLayout={HorizontalLayout.Center} gap="10px">
          <ToggleField
            label={Locale.translate(Translation.TC_PASSPORT_EU)}
            checked={user.tradingEntityDetails.every(t => t.euPassport)}
            onUpdate={handleUpdateAllDetails('euPassport')}
            readOnly={readOnly}
          />
          <ToggleField
            label={Locale.translate(Translation.TC_PASSPORT_UK)}
            checked={user.tradingEntityDetails.every(t => t.ukPassport)}
            onUpdate={handleUpdateAllDetails('ukPassport')}
            readOnly={readOnly}
          />
          <ToggleField
            label={Locale.translate(Translation.TC_PASSPORT_NIUK)}
            checked={user.tradingEntityDetails.every(t => t.niUk)}
            onUpdate={handleUpdateAllDetails('niUk')}
            readOnly={readOnly}
          />
        </DialogRow>
      )}

      {user.tradingEntityDetails.length > 0 && <Table columns={columns} rows={user.tradingEntityDetails} options={options} />}

      {user.tradingEntityDetails.length > 0 && !readOnly && (
        <ClearButton onClick={() => handleUpdateTradingEntities([])}>
          {Locale.translate(Translation.SC_REMOVE_TRADING_ENTITIES)}
        </ClearButton>
      )}
    </>
  )
}
